/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import PropTypes, { any } from 'prop-types';

import InfoButton from '../InfoButton';
import DivisionSelectionV2 from './DivisionSelectionV2';

import { eventDivisionsContainPlatformPaymentOrFree, displayFee, canUserRegisterForDivisionBasedOnGender, isDivisionCompletelyRegistered, isDivisionCustomRegistrationDateClosed } from '../../utils/events';
import { getClickAttrs } from '../Analytics/gtag';
import { divisionGenderTypes, eventStateTypeIds, paymentProviderTypeIds, regStepIds, divisionRegistrationTypeIds, sportTypeIds, paymentStatusTypeIds, registrationStatusTypeIds, registrationGroupStatusTypeIds, registrationStateTypeIds, registrationUserOptions } from '../../utils/eventConstants';
import { setSelectedDivisionsBasedOnDate } from '../../actions/event-profile-actions';
import { SubscriptionIntervalTypes } from '../../utils/constants';
import { trackEvent } from '../Analytics/TrackedClick';

import styles from './eventRegForm.scss';

// supports V2 typeIds
const AddDivision = (props) => {
    const {
        user,
        event,
        eventFee,
        eventEntryFee,
        isEventEntryFeePaid,
        selectedDivisionIds = [],
        clubVerificationFee,
        subtotal,
        registrationTotal,
        registeredDivisionDates,
        isEditRegisterationPage,
        updateErrorMsgStatus,
        sidebarFlow,
        serviceFee,
        displayServiceFee,
        divisionFormatTypeFilter,
        canceledDivisionIds,
        switchPlayer2,
        handleCloseClick,
        showPreRegAlternateCopy,
        players,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { types: { tms: { genderTypes } } } = coreUI;
    const { selectedDivisionIdsByDate, registrations } = useSelector(({ eventProfile }) => eventProfile);
    const dispatch = useDispatch();
    const isV2Event = !!event.utrTypeId;
    const isAnySelectedDivisionAnnualPowerOnly = (event?.eventDivisions) ? _.findIndex(event.eventDivisions, { selected: true, annualPoweredOnly: true }) !== -1 : false;
    const userHasAnnualPower = user?.isPowered && user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR;
    const requirePowerUpgradeForRegistration = !userHasAnnualPower && isAnySelectedDivisionAnnualPowerOnly;
    const isInPreRegState = event.eventStateId === eventStateTypeIds.PRE_REGISTRATION;
    const isRegClosed = event?.registrationStateId === registrationStateTypeIds.REGISTRATION_CLOSED;
    const isSwitchFlow = sidebarFlow === regStepIds.DIVISIONS_SWITCH;
    const isRegistered = event.registered || event.currentMember.isPlaying;
    const hasPreRegInvites = event?.currentMember?.memberPreRegistrationInvites?.length > 0;
    const isUserEligibleForEvent = event?.currentMember?.isEligibleForEvent;
    const isRegistrationOpen = event?.eventStateId === eventStateTypeIds.OPEN_REGISTRATION;
    const isEligibleListEnabled = event?.preRegAndEligibleListEnabled;

    const [elligibleDivisions, setElligibleDivisions] = useState({});
    const [divisionsLoaded, setDivisionsLoaded] = useState(false);
    const [showDivisionHeading, setShowDivisionHeading] = useState(false);
    const [hideThisDate, setHideThisDate] = useState(null);
    const [canceledDivisionDates, setCanceledDivisionDates] = useState([]);
    const [showDate, setShowDate] = useState({});

    const doesMemberExist = Object.values(players?.playersDict).some((player) => player.memberId === user?.id);
    const distinctDivisionRegistrationTypeIds = [...new Set(event?.currentMember?.registeredDivisions?.filter((div) => !isDivisionCompletelyRegistered(div, event)).map(division => division.divisionRegistrationTypeId))];
    const onlyHasAdminAddedDivisions = distinctDivisionRegistrationTypeIds?.length === 1 && distinctDivisionRegistrationTypeIds[0] === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED && !hasPreRegInvites;
    const adminAddedPaymentRequiredDivisionIds = [...new Set(event?.currentMember?.registeredDivisions?.filter(division => division.divisionRegistrationTypeId === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED).map(division => division.id))];
    const pendingRegistrationDivisionIds = [...new Set(event?.currentMember?.registeredDivisions?.filter(division => division.registrationStatusTypeId === registrationStatusTypeIds.PENDING || division.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING).map(division => division.id))];
    const registeredDivs = registrations?.data?.registeredDivisions || [];
    const registeredDivIds = registrations?.data?.registeredDivisions?.map(div => div.divisionId) || [];
    const availableDivIds = registrations?.data?.availableDivisions?.map(div => div.divisionId) || [];
    const eventEntryFeeDivision = registeredDivs.find(div => div.isEventEntryFeeDivision);
    const preventSameDate = event?.preventDivisionDateRegistrationConflicts;
    const preventProAndAmateur = event?.preventProAmDivisionRegistrationConflicts;
    const noDateAssigned = 'NO DATE ASSIGNED';
    const currencySymbol = event?.currency?.symbol || '$';
    const isIphone = /iPhone/i.test(navigator.userAgent);
    const isSafari = /Safari\/[0-9.]+/.test(navigator.userAgent) && !/Chrome|CriOS|Android|Edg|OPR/i.test(navigator.userAgent);

    const shouldDisplayDivision = (division) => {
        let shouldDisplay = true;
        if (isPlayerAddedWithPaymentRequired(division, event)) {
            return shouldDisplay;
        }
        if (isSwitchFlow) {
            const regDiv = registeredDivs.find(div => canceledDivisionIds[0] === div.divisionId);
            shouldDisplay = regDiv.availableDivisionsForSwitching.find(div => div.divisionId === division.id);
        } else if (isV2Event) {
            shouldDisplay = canUserRegisterForDivisionBasedOnGender(division, user, genderTypes);
        } else if (division?.gender?.value === divisionGenderTypes.ALL || division?.gender?.value === divisionGenderTypes.MIXED) {
            // Do nothing
        } else {
            shouldDisplay = division.gender?.label?.toLowerCase() === user?.gender?.toLowerCase();
        }

        // check if there is an age restriction on the event
        if (division.restrictAge || division.restrictByRating) {
            // use the dictionary from the UseEffect to determine if the user can register based on age
            if (shouldDisplay && elligibleDivisions) {
                shouldDisplay = elligibleDivisions[division.id];
            }
        }
        // hide non-excluded divisions if eligible list is enabled and the user is not on the eligible list
        if (isRegistrationOpen && isEligibleListEnabled && !isUserEligibleForEvent && !division.excludeFromEligibleListRestrictions) {
            shouldDisplay = false;
        }
        return shouldDisplay;
    };
    const shouldDisableDivision = (division) => {
        const shouldDisplay = shouldDisplayDivision(division);
        if (!shouldDisplay) {
            return true;
        }

        if (adminAddedPaymentRequiredDivisionIds.includes(division.id)) {
            return true;
        }

        const { maxPlayers, registeredCount, allowWaitlist, divisionFormatTypeId } = division;

        let isLimitExceeded = false;
        if (maxPlayers && registeredCount && maxPlayers <= registeredCount) {
            isLimitExceeded = true;
        }

        if ((isLimitExceeded || (parseInt(event?.sportTypeId) === sportTypeIds.PICKLEBALL && maxPlayers === 0)) && !allowWaitlist) {
            return true;
        }

        if (divisionFormatTypeFilter && divisionFormatTypeFilter !== divisionFormatTypeId) {
            return true;
        }

        return !userHasAnnualPower && division.annualPoweredOnly;
    };

    const onDivisionSelectionContinueClick = () => {
        if (isSwitchFlow) {
            props.toStep(regStepIds.DIVISIONS_SWITCH);
        } else {
            const playersToShowQuestions = props.unRegisteredPlayers?.filter((player) => {
                return player.canAnswerEventQuestions;
            });
            const shouldShowPartnerQuestions = playersToShowQuestions.length > 0;

            const isCurrentMemberInUnregisteredPlayers = props.unRegisteredPlayers?.find((player) => {
                return player.isCurrentUser;
            });
            const shouldShowSelfQuestions = !isCurrentMemberInUnregisteredPlayers && event.currentMember?.answers?.length < event.eventQuestions?.length;

            const eventHasQuestions = event?.eventQuestions?.length > 0;

            const shouldShowSmsOptIn = false; // TODO: later this will be based on event setting (not yet implemented)

            if (shouldShowSmsOptIn || (eventHasQuestions && (shouldShowPartnerQuestions || shouldShowSelfQuestions))) {
                props.toStep('questions');
            } else {
                props.toStep('confirm');
            }
        }
        selectedDivisionIds.forEach((divisionId) => {
            trackEvent('Divisions Selected', {
                memberId: user?.id,
                eventId: event?.id,
                divisionId,
                registrationType: registrationUserOptions.SINGLE_PLAYER,
            });
        });
    };

    const isAdminAddedPaymentRequired = (division) => {
        return adminAddedPaymentRequiredDivisionIds.includes(division.id);
    };

    const isRegistrationPending = (division) => {
        return pendingRegistrationDivisionIds.includes(division.id);
    };

    const isInRegistrationInProgressAdminAddedPaymentRequired = (division => {
        return event?.currentMember?.registeredDivisions?.filter((div) => !isDivisionCompletelyRegistered(div, event) &&
            div.divisionRegistrationTypeId === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED).map(div => div.id).includes(division.id);
    });

    const isPlayerAddedWithPaymentRequired = (division, event) => {
        const currentMemberDivision = event.currentMember.registeredDivisions?.find(div => div.id === division.id);
        const { divisionRegistrationTypeId } = currentMemberDivision || {};
        return divisionRegistrationTypeId === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED;
    };

    useEffect(() => {
        // only call the api if there is at least one age restricted division found
        const anyRestrictedDivisions = event.eventDivisions.some(division => division.restrictAge === true || division.restrictByRating === true);
        if (anyRestrictedDivisions) {
            const getDivisions = async () => {
                const data = await props.getRestrictedDivisions(event.id);
                setElligibleDivisions(data);
                setDivisionsLoaded(true);
            };
            getDivisions();
        } else {
            setDivisionsLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (divisionsLoaded) {
            const eventDivisions = event?.eventDivisions || [];
            const divisionsLength = {};
            // canceled divisions during switch division flow
            const canceledDivisionDatesUpdate = [];
            const adminAddedPaymentRequiredDivisionIds = [...new Set(event?.currentMember?.registeredDivisions?.filter(division => division.divisionRegistrationTypeId === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED && division.paymentStatusTypeId !== paymentStatusTypeIds.PAID).map(division => division.id))];

            eventDivisions.forEach((div) => {
                const date = div?.specificDivisionDateUtc ? new Date(div?.specificDivisionDateUtc).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : noDateAssigned;
                divisionsLength[date] = (divisionsLength[date] || 0) + 1;
                if (canceledDivisionIds?.length) {
                    const formateDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    if (canceledDivisionIds.includes(div.id) && div.specificDivisionDateUtc) {
                        canceledDivisionDatesUpdate.push(formateDate(div.specificDivisionDateUtc));
                    }
                }
            });

            if (canceledDivisionDatesUpdate.length) {
                setCanceledDivisionDates(canceledDivisionDatesUpdate);
            }

            if (selectedDivisionIds?.length === 0) {
                const divisionsToSelectByIndex = [];
                eventDivisions.forEach((div, i) => {
                    if (!isDivisionCompletelyRegistered(div, event) && ((div.player2 && div.isInvited) || adminAddedPaymentRequiredDivisionIds.includes(div.id))) {
                        const date = div?.specificDivisionDateUtc ? new Date(div?.specificDivisionDateUtc).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : noDateAssigned;
                        divisionsToSelectByIndex.push(i);
                        if (date !== noDateAssigned) {
                            if (selectedDivisionIdsByDate?.[date] === div.id || divisionsLength?.[date] === 1) {
                                dispatch(setSelectedDivisionsBasedOnDate({
                                    [date]: null,
                                    selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],

                                }));
                            } else {
                                dispatch(setSelectedDivisionsBasedOnDate({
                                    [date]: div.id,
                                    selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],

                                }));
                            }
                        }
                        if (div?.divisionCategoryModel?.value) {
                            dispatch(setSelectedDivisionsBasedOnDate({
                                selectedDivisions: [...(selectedDivisionIdsByDate?.selectedDivisions || []), div],
                            }));
                        }
                    }
                });
                if (divisionsToSelectByIndex.length) {
                    props.toggleDivision(null, divisionsToSelectByIndex);
                }
            }
        }
    }, [divisionsLoaded]);

    function categorizeByDate(dataArray) {
        if (!dataArray) {
            return {};
        }
        const newArray = [...dataArray];
        const categorizedData = {};

        newArray.forEach((obj, i) => {
            const date = obj.specificDivisionDateUtc ? new Date(obj.specificDivisionDateUtc).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : noDateAssigned;
            if (categorizedData[date]) {
                categorizedData[date].push({ division: obj, i });
            } else {
                categorizedData[date] = [{ division: obj, i }];
            }
        });

        // Extract keys (dates) from the categorizedData object
        const dates = Object.keys(categorizedData)
            .filter(date => date !== noDateAssigned)
            .map(date => new Date(date))
            .sort((a, b) => a - b);

        // Create a new object with sorted keys
        const sortedCategorizedData = {};
        dates.forEach(date => {
            const formattedDate = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            sortedCategorizedData[formattedDate] = categorizedData[formattedDate];
        });
        if (categorizedData[noDateAssigned]) {
            sortedCategorizedData[noDateAssigned] = categorizedData[noDateAssigned];
        }
        return sortedCategorizedData;
    }

    function reorderNoDateAssignedToBottom(dataArray) {
        if (!dataArray) {
            return [];
        }
        const newArray = [...dataArray];

        const noDateAssignedIndex = newArray.findIndex(([key]) => key === noDateAssigned);

        // If "NO DATE ASSIGNED" is found, remove it from the array
        if (noDateAssignedIndex !== -1) {
            const noDateAssignedEntry = newArray.splice(noDateAssignedIndex, 1)[0];
            // Append the "NO DATE ASSIGNED" entry at the end of the array
            newArray.push(noDateAssignedEntry);
        }
        return newArray;
    }
    let registeredDivisionCategory = 0;
    let mostRecentNonNullCategory = null;
    event.eventDivisions.forEach((division, index) => {
        if (isDivisionCompletelyRegistered(division, event) && !!preventProAndAmateur) {
            if (division?.divisionCategory !== null) {
                mostRecentNonNullCategory = division.divisionCategory;
            }
            registeredDivisionCategory = division?.divisionCategory;
        }
        if (index === event.eventDivisions?.length - 1 && registeredDivisionCategory === null) {
            registeredDivisionCategory = mostRecentNonNullCategory;
        }
    });
    const eventsByDate = Object.entries(categorizeByDate(event?.eventDivisions));
    const reorderlist = reorderNoDateAssignedToBottom(eventsByDate);

    useEffect(() => {
        if (selectedDivisionIdsByDate && Object.values(selectedDivisionIdsByDate).some(value => Number.isFinite(value) && value !== null)) {
            setShowDivisionHeading(true);
            updateErrorMsgStatus(true);
        } else {
            setShowDivisionHeading(false);
            updateErrorMsgStatus(false);
        }
    }, [selectedDivisionIdsByDate]);

    const divisionWithCategory = !!selectedDivisionIdsByDate?.selectedDivisions?.[0]?.divisionCategory;
    const showCategoryMsg = preventProAndAmateur && divisionWithCategory;
    const isEventCompleted = event?.eventStateId === eventStateTypeIds.COMPLETED;

    const groupedByDate = (preventSameDate || preventProAndAmateur);

    return (
        <>
            {isSwitchFlow ? (
                <div className="mb16">
                    <div className="text-highlight2 text-dark">Choose division to switch to</div>
                </div>
            ) : (
                <>
                    {!onlyHasAdminAddedDivisions && showDivisionHeading && preventSameDate && !isEditRegisterationPage && (
                        <div className="text-dark fs14 mb8">
                            This event limits registration to:
                            <li className="ml12"> One division per day</li>
                            {showCategoryMsg && <li className="ml12"> Either amateur or pro divisions only</li>}
                            Based on your selection, restricted divisions are hidden. Deselect to choose a different option.
                            <div className="div div16" />
                        </div>
                    )}
                    {!onlyHasAdminAddedDivisions && !showDivisionHeading && showCategoryMsg && !isEditRegisterationPage && (
                        <div className="text-dark fs14 mb8">
                            This event limits registration to:
                            <li className="ml12"> One division per day</li>
                            <li className="ml12"> Either amateur or pro divisions only</li>
                            Based on your selection, restricted divisions are hidden. Deselect to choose a different option.
                            <div className="div div16" />
                        </div>
                    )}
                </>
            )}

            {!isSwitchFlow && isRegistered && (
                <>
                    {!hasPreRegInvites && !showPreRegAlternateCopy && <div className="text-highlight3 text-center mb16">Edit Registration</div>}
                    {event.eventDivisions.filter((div) => registeredDivIds.includes(div.id)).map((division) => {
                        // registered divisions listed at the top
                        return (
                            <DivisionSelectionV2
                                key={`registered-division-${division.id}`}
                                division={division}
                                event={event}
                                selected
                                shouldShowGenderInSubTitle={false}
                                shouldDisplayTeamPaymentType
                                shouldDisplayAgeRestrictionText={false}
                                shouldDisplayRangeRestrictionText={false}
                                canChangePartner={false}
                                disabled
                                clubVerificationFee={clubVerificationFee}
                                doesMemberExist={doesMemberExist}
                                userIsRegisteredToDivision
                            />
                        );
                    })}
                </>
            )}

            {/* PRE-REG & OPEN REG - player only has admin added divisions - not grouped */}
            {/* We hide all divisions and only show the admin invites, never grouped */}

            {onlyHasAdminAddedDivisions && divisionsLoaded && event?.eventDivisions.map((division, i) => {
                const shouldDisplayDivisionToUser = shouldDisplayDivision(division);
                const divisionIsCompletelyRegistered = isDivisionCompletelyRegistered(division, event);
                const divisionPaymentRequired = isAdminAddedPaymentRequired(division);
                const registrationIsPending = isRegistrationPending(division);
                const isDivisionClosed = isDivisionCustomRegistrationDateClosed(division);
                const isDivisionIncludedInPreReg = event.currentMember?.memberPreRegistrationInvites?.some(invite => invite.availableDivisionIds.includes(division.id));
                // pre-select payment-required invites only (pre-reg invites are optional)
                const selected = (shouldDisplayDivisionToUser && !isDivisionIncludedInPreReg) || selectedDivisionIds?.includes(division.id);
                const shouldHideDivisionBecauseClosed = isDivisionClosed && !isInRegistrationInProgressAdminAddedPaymentRequired(division);

                if (!shouldHideDivisionBecauseClosed && (divisionPaymentRequired || isDivisionIncludedInPreReg) && !divisionIsCompletelyRegistered && shouldDisplayDivisionToUser) {
                    return (
                        <DivisionSelectionV2
                            key={`add-division-selection-${division.id}`}
                            disabled={shouldDisableDivision(division)}
                            division={division}
                            divisionIndex={i}
                            event={event}
                            selected={selected}
                            shouldShowGenderInSubTitle
                            shouldDisplayTeamPaymentType
                            shouldDisplayAgeRestrictionText
                            shouldDisplayRangeRestrictionText
                            canChangePartner={!!division.player2?.needsPartner}
                            handleClick={() => props.toggleDivision(i)}
                            toStep={props.toStep}
                            declinePartnerInvitation={props.declinePartnerInvitation}
                            clubVerificationFee={clubVerificationFee}
                            isAdminAddedPaymentRequired={divisionPaymentRequired && registrationIsPending}
                            handleCloseClick={handleCloseClick}
                            doesMemberExist={doesMemberExist}
                        />
                    );
                }
                return null;
            })}

            {/* PRE-REG with eligible players list - not grouped */}

            {isInPreRegState && !onlyHasAdminAddedDivisions && event?.hasEligiblePlayersList && !groupedByDate && divisionsLoaded && event?.eventDivisions.map((division, i) => {
                const userIsRegisteredToDivision = isDivisionCompletelyRegistered(division, event);
                const shouldDisplayDivisionToUser = shouldDisplayDivision(division);
                const divisionIsCompletelyRegistered = isDivisionCompletelyRegistered(division, event);
                const isDivisionClosed = isDivisionCustomRegistrationDateClosed(division);
                const divisionPaymentRequired = isAdminAddedPaymentRequired(division);
                const registrationIsPending = isRegistrationPending(division);
                const isDivisionIncludedInPreReg = event.currentMember?.memberPreRegistrationInvites?.some(invite => invite.availableDivisionIds.includes(division.id)) && !isDivisionClosed;
                const isDivisionIncludedInPartnerInvites = (!divisionIsCompletelyRegistered && shouldDisplayDivisionToUser) && event.currentMember?.registeredDivisions?.some(div => div.id === division.id);
                const selected = !userIsRegisteredToDivision && shouldDisplayDivisionToUser && selectedDivisionIds?.includes(division.id);
                const shouldHideDivisionBecauseClosed = isDivisionClosed && !isInRegistrationInProgressAdminAddedPaymentRequired(division);
                if (!shouldHideDivisionBecauseClosed && (isDivisionIncludedInPreReg || isDivisionIncludedInPartnerInvites)) {
                    return (
                        <DivisionSelectionV2
                            key={`add-division-selection-${division.id}`}
                            disabled={shouldDisableDivision(division)}
                            division={selected && switchPlayer2 ? {
                                ...division,
                                player2: switchPlayer2,
                            } : division}
                            divisionIndex={i}
                            event={event}
                            selected={selected}
                            shouldShowGenderInSubTitle
                            shouldDisplayTeamPaymentType
                            shouldDisplayAgeRestrictionText
                            shouldDisplayRangeRestrictionText
                            canChangePartner={!isSwitchFlow}
                            handleClick={() => props.toggleDivision(i)}
                            toStep={props.toStep}
                            declinePartnerInvitation={props.declinePartnerInvitation}
                            clubVerificationFee={clubVerificationFee}
                            doesMemberExist={doesMemberExist}
                            isAdminAddedPaymentRequired={divisionPaymentRequired && registrationIsPending}
                        />
                    );
                }
                return null;
            })}

            {/* PRE-REG & eligible list events - grouped (prevent conflicts) */}

            {isInPreRegState && !onlyHasAdminAddedDivisions && event?.hasEligiblePlayersList && groupedByDate && divisionsLoaded && reorderlist?.map(([date, divisions]) => {
                const selectedDivisionIdInDate = selectedDivisionIdsByDate?.[date] || null;
                let hideDate = false;
                if (isSwitchFlow) {
                    hideDate = registeredDivisionDates?.includes(date) && !canceledDivisionDates?.includes(date);
                } else {
                    hideDate = registeredDivisionDates?.includes(date);
                }
                if (hideDate) {
                    return null;
                }
                const findThisDateHasNoSelectedCateg = (date) => {
                    setHideThisDate((prev) => {
                        return {
                            ...prev,
                            [date]: true,
                        };
                    });
                    divisions.forEach((div) => {
                        if ((selectedDivisionIdsByDate && Object.keys(selectedDivisionIdsByDate).some(value => value === date)) || !div?.division?.divisionCategory) {
                            setHideThisDate((prev) => {
                                return {
                                    ...prev,
                                    [date]: false,
                                };
                            });
                        }
                    });
                };
                return (
                    <div key={date}>
                        {showDate?.[date] && preventSameDate && (
                            <div className="fs14 mb4 text-bold text-dark">
                                {date}
                            </div>
                        )}
                        {divisions.map(({ division, i }) => {
                            const divisionIsCompletelyRegistered = isDivisionCompletelyRegistered(division, event);
                            const isDivisionIncludedInPreReg = event.currentMember?.memberPreRegistrationInvites?.some(invite => invite.availableDivisionIds.includes(division.id));
                            const userIsRegisteredToDivision = isDivisionCompletelyRegistered(division, event);
                            const shouldDisplayDivisionToUser = shouldDisplayDivision(division);
                            const isDivisionIncludedInPartnerInvites = (!divisionIsCompletelyRegistered && shouldDisplayDivisionToUser) && event.currentMember?.registeredDivisions?.some(div => div.id === division.id);
                            const hasSameCategoryAsRegisteredDiv = (division.divisionCategory && registeredDivisionCategory) ? division.divisionCategory === registeredDivisionCategory : true;
                            const sameCategoryDivisionIsSelected = selectedDivisionIdsByDate && selectedDivisionIdsByDate?.selectedDivisions?.length > 0 ? selectedDivisionIdsByDate?.selectedDivisions.some(div => div?.divisionCategory === division?.divisionCategory) : true;
                            const sameCategoryDivisionSelected = sameCategoryDivisionIsSelected || !division?.divisionCategory || !preventProAndAmateur;
                            const showDivisionInDate = !selectedDivisionIdInDate || selectedDivisionIdInDate === division.id || !selectedDivisionIdsByDate || isSwitchFlow || !preventSameDate;
                            const isDivisionClosed = isDivisionCustomRegistrationDateClosed(division);
                            const divisionPaymentRequired = isAdminAddedPaymentRequired(division);
                            const registrationIsPending = isRegistrationPending(division);
                            const selected = selectedDivisionIds?.includes(division.id);
                            if ((isDivisionIncludedInPreReg || isDivisionIncludedInPartnerInvites) && !userIsRegisteredToDivision && shouldDisplayDivisionToUser &&
                                ((sameCategoryDivisionSelected && showDivisionInDate && hasSameCategoryAsRegisteredDiv && !isDivisionClosed) || isInRegistrationInProgressAdminAddedPaymentRequired(division))) {
                                if (!showDate?.[date]) {
                                    setShowDate((prevState) => {
                                        return { ...prevState, [date]: true };
                                    });
                                }
                                return (
                                    <DivisionSelectionV2
                                        key={`add-division-selection-${division.id}`}
                                        disabled={shouldDisableDivision(division)}
                                        division={selected && switchPlayer2 ? {
                                            ...division,
                                            player2: switchPlayer2,
                                        } : division}
                                        divisionIndex={i}
                                        event={event}
                                        selected={selected}
                                        shouldShowGenderInSubTitle
                                        shouldDisplayTeamPaymentType
                                        shouldDisplayAgeRestrictionText
                                        shouldDisplayRangeRestrictionText
                                        canChangePartner={!isSwitchFlow}
                                        handleClick={() => {
                                            props.toggleDivision(i);
                                            if (date !== noDateAssigned && !isInRegistrationInProgressAdminAddedPaymentRequired(division)) {
                                                if (selectedDivisionIdInDate === division.id || divisions?.length === 1) {
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        [date]: null,
                                                        selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],
                                                    }));
                                                } else {
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        [date]: division.id,
                                                        selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],
                                                    }));
                                                }
                                            }
                                            if (division?.divisionCategoryModel?.value && !isInRegistrationInProgressAdminAddedPaymentRequired(division)) {
                                                if (selectedDivisionIdsByDate?.selectedDivisions?.some(div => div.id === division?.id)) {
                                                    const filteredDivs = selectedDivisionIdsByDate?.selectedDivisions.filter(div => div.id !== division.id);
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        selectedDivisions: filteredDivs,
                                                    }));
                                                } else {
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        selectedDivisions: [...(selectedDivisionIdsByDate?.selectedDivisions || []), division],
                                                    }));
                                                }
                                            }
                                            if (!isInRegistrationInProgressAdminAddedPaymentRequired(division)) {
                                                findThisDateHasNoSelectedCateg(date);
                                            }
                                        }}
                                        toStep={props.toStep}
                                        declinePartnerInvitation={props.declinePartnerInvitation}
                                        clubVerificationFee={clubVerificationFee}
                                        preventDivisionDateRegistrationConflicts={true}
                                        doesMemberExist={doesMemberExist}
                                        isAdminAddedPaymentRequired={divisionPaymentRequired && registrationIsPending}
                                    />
                                );
                            }
                            return null;
                        })}
                    </div>
                );
            })}

            {/* OPEN REG - not only admin invites - not grouped */}

            {!isInPreRegState && event && !onlyHasAdminAddedDivisions && !groupedByDate && divisionsLoaded && event.eventDivisions.map((division) => {
                const selected = selectedDivisionIds?.includes(division.id);
                const index = event.eventDivisions?.findIndex(div => div.id === division.id);
                const divisionPaymentRequired = isAdminAddedPaymentRequired(division);
                const registrationIsPending = isRegistrationPending(division);
                const isDivisionClosed = isDivisionCustomRegistrationDateClosed(division);
                const shouldHideDivisionBecauseClosed = isDivisionClosed && !isInRegistrationInProgressAdminAddedPaymentRequired(division);
                const shouldDisplayDivisionToUser = availableDivIds.includes(division.id) && !shouldHideDivisionBecauseClosed && (shouldDisplayDivision(division) || isPlayerAddedWithPaymentRequired(division, event));
                return shouldDisplayDivisionToUser ? (
                    <DivisionSelectionV2
                        key={`add-division-selection-${division.id}`}
                        disabled={shouldDisableDivision(division)}
                        division={selected && switchPlayer2 ? {
                            ...division,
                            player2: switchPlayer2,
                        } : division}
                        divisionIndex={index}
                        event={event}
                        selected={selected}
                        shouldShowGenderInSubTitle
                        shouldDisplayTeamPaymentType
                        shouldDisplayAgeRestrictionText
                        shouldDisplayRangeRestrictionText
                        canChangePartner={!isSwitchFlow}
                        handleClick={() => props.toggleDivision(index)}
                        toStep={props.toStep}
                        declinePartnerInvitation={props.declinePartnerInvitation}
                        clubVerificationFee={clubVerificationFee}
                        isAdminAddedPaymentRequired={divisionPaymentRequired && registrationIsPending}
                        handleCloseClick={handleCloseClick}
                        doesMemberExist={doesMemberExist}
                    />
                ) : null;
            })}

            {/* OPEN REG - not only admin invites - grouped (prevent conflicts) */}

            {!isInPreRegState && !onlyHasAdminAddedDivisions && groupedByDate && divisionsLoaded && reorderlist?.map(([date, divisions]) => {
                const selectedDivisionIdInDate = selectedDivisionIdsByDate?.[date] || null;
                let hideDate = false;
                if (isSwitchFlow) {
                    hideDate = registeredDivisionDates?.includes(date) && !canceledDivisionDates?.includes(date);
                } else {
                    hideDate = registeredDivisionDates?.includes(date);
                }

                if (hideDate) {
                    return null;
                }
                const hideDateHeader = reorderlist.length === 1 && reorderlist[0][0] === noDateAssigned;
                let shouldRenderDate = false;
                const updateDoWeDisplayAtleastOneDiv = () => {
                    if (divisions?.length === 1 && divisions[0]?.division?.isEventEntryFeeDivision === true) { return false; }
                    for (let i = 0; i < divisions.length; i += 1) {
                        const div = divisions[i].division;
                        const userIsRegisteredToDivision = isDivisionCompletelyRegistered(div, event);
                        const shouldDisplayDivisionToUser = shouldDisplayDivision(div);
                        if (!userIsRegisteredToDivision && shouldDisplayDivisionToUser) {
                            return true;
                        }
                    }
                    return false;
                };
                const doWeDisplayAtleastOneDiv = updateDoWeDisplayAtleastOneDiv();

                const findThisDateHasNoSelectedCateg = (date) => {
                    setHideThisDate((prev) => {
                        return {
                            ...prev,
                            [date]: true,
                        };
                    });
                    divisions.forEach((div) => {
                        if ((selectedDivisionIdsByDate && Object.keys(selectedDivisionIdsByDate).some(value => value === date)) || !div?.division?.divisionCategory) {
                            setHideThisDate((prev) => {
                                return {
                                    ...prev,
                                    [date]: false,
                                };
                            });
                        }
                    });
                };

                const isConditionForNoDateAssignedMet = hideDateHeader || (date !== noDateAssigned && !doWeDisplayAtleastOneDiv);
                const isConditionForAtleastOneDivMet = (date === noDateAssigned && !doWeDisplayAtleastOneDiv);
                const isConditionForSameDateMet = hideThisDate?.[date];
                const shouldHide = isConditionForNoDateAssignedMet || isConditionForAtleastOneDivMet || isConditionForSameDateMet;
                if (shouldHide) {
                    shouldRenderDate = false;
                } else {
                    shouldRenderDate = true;
                }
                return (
                    <div key={date}>
                        {shouldRenderDate && preventSameDate && (
                            <div className="fs14 mb4 text-bold text-dark">
                                {date}
                            </div>
                        )}
                        {divisions.map(({ division, i }) => {
                            const hasSameCategoryAsRegisteredDiv = (division.divisionCategory && registeredDivisionCategory) ? division.divisionCategory === registeredDivisionCategory : true;
                            const sameCategoryDivisionIsSelected = selectedDivisionIdsByDate && selectedDivisionIdsByDate?.selectedDivisions?.length > 0 ? selectedDivisionIdsByDate?.selectedDivisions.some(div => div?.divisionCategory === division?.divisionCategory) : true;
                            const userIsRegisteredToDivision = isDivisionCompletelyRegistered(division, event);
                            const shouldDisplayDivisionToUser = shouldDisplayDivision(division);
                            const sameCategoryDivisionSelected = sameCategoryDivisionIsSelected || !division?.divisionCategory || !preventProAndAmateur;
                            const showDivisionInDate = !selectedDivisionIdInDate || selectedDivisionIdInDate === division.id || !selectedDivisionIdsByDate || isSwitchFlow || !preventSameDate;
                            const selected = selectedDivisionIds?.includes(division.id);
                            const isDivisionClosed = isDivisionCustomRegistrationDateClosed(division);
                            const divisionPaymentRequired = isAdminAddedPaymentRequired(division);
                            const registrationIsPending = isRegistrationPending(division);
                            const registeredDivs = event.eventDivisions.filter((div) => registeredDivIds.includes(div.id));
                            const isCategoryMismatch = preventProAndAmateur && (division?.divisionCategory && registeredDivs.some((div) => ((div.divisionFormatTypeId === division.divisionFormatTypeId) && !!div.divisionCategory && div.divisionCategory !== division.divisionCategory)));
                            if (!isCategoryMismatch && !userIsRegisteredToDivision && shouldDisplayDivisionToUser &&
                                ((sameCategoryDivisionSelected && showDivisionInDate && hasSameCategoryAsRegisteredDiv && !isDivisionClosed) || isInRegistrationInProgressAdminAddedPaymentRequired(division))) {
                                return (
                                    <DivisionSelectionV2
                                        key={`add-division-selection-${division.id}`}
                                        disabled={shouldDisableDivision(division)}
                                        division={selected && switchPlayer2 ? {
                                            ...division,
                                            player2: switchPlayer2,
                                        } : division}
                                        divisionIndex={i}
                                        event={event}
                                        selected={selected}
                                        shouldShowGenderInSubTitle
                                        shouldDisplayTeamPaymentType
                                        shouldDisplayAgeRestrictionText
                                        shouldDisplayRangeRestrictionText
                                        canChangePartner={!isSwitchFlow}
                                        handleClick={() => {
                                            props.toggleDivision(i);
                                            if (date !== noDateAssigned && !isInRegistrationInProgressAdminAddedPaymentRequired(division)) {
                                                if (selectedDivisionIdInDate === division.id || divisions?.length === 1) {
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        [date]: null,
                                                        selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],
                                                    }));
                                                } else {
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        [date]: division.id,
                                                        selectedDivisions: selectedDivisionIdsByDate?.selectedDivisions || [],
                                                    }));
                                                }
                                            }
                                            if (division?.divisionCategoryModel?.value && !isInRegistrationInProgressAdminAddedPaymentRequired(division)) {
                                                if (selectedDivisionIdsByDate?.selectedDivisions?.some(div => div.id === division?.id)) {
                                                    const filteredDivs = selectedDivisionIdsByDate?.selectedDivisions.filter(div => div.id !== division.id);
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        selectedDivisions: filteredDivs,
                                                    }));
                                                } else {
                                                    dispatch(setSelectedDivisionsBasedOnDate({
                                                        selectedDivisions: [division],
                                                    }));
                                                }
                                            }
                                            if (!isInRegistrationInProgressAdminAddedPaymentRequired(division)) {
                                                findThisDateHasNoSelectedCateg(date);
                                            }
                                        }}
                                        toStep={props.toStep}
                                        declinePartnerInvitation={props.declinePartnerInvitation}
                                        clubVerificationFee={clubVerificationFee}
                                        preventDivisionDateRegistrationConflicts={true}
                                        doesMemberExist={doesMemberExist}
                                        isAdminAddedPaymentRequired={divisionPaymentRequired && registrationIsPending}
                                    />
                                );
                            }
                            return null;
                        })}
                    </div>
                );
            })}

            {divisionsLoaded && event.eventDivisions.every(division => !shouldDisplayDivision(division)) && (
                <div>
                    No event divisions are available for your registration due to player eligibility criteria.
                </div>
            )}
            <div key={`${eventFee}-${subtotal}`} className={`${styles.divisionsSelectSummaryWrapper} ${isIphone ? styles.divisionsSelectSummaryWrapperIphone : ''} ${isSafari ? styles.divisionsSelectSummaryWrapperSafari : ''}`}>
                {!isSwitchFlow && event.currency && (
                    <div className="mt32 mb24">
                        {(eventDivisionsContainPlatformPaymentOrFree(event.eventDivisions) && !event.allowOffPlatformPayments) ? (
                            <>
                                {eventEntryFee > 0 && (
                                    <div>
                                        Player entry fee
                                        <span className="float-right" data-testid="event-reg.player-entry-fee">{
                                            displayFee(
                                                currencySymbol,
                                                eventEntryFeeDivision?.total && eventEntryFeeDivision.total !== eventEntryFee ? eventEntryFeeDivision.total : eventEntryFee,
                                                isEventEntryFeePaid,
                                            )
}
                                        </span>
                                    </div>
                                )}
                                <div>
                                    Division fee <span className="float-right" data-testid="event-reg.division-fee">{displayFee(currencySymbol, eventFee, false)}</span>
                                </div>
                                {props.paymentProviderType === paymentProviderTypeIds.WEPAY && props.financeAndAdminFee && (
                                    <div className="mt4">
                                        Processing fees <InfoButton translateXValue="70%" hoverText={'Payment processing fees.'} /> <span className="float-right">{event.currency.symbol}{props.financeAndAdminFee.toFixed(2)}</span>
                                    </div>
                                )}
                                {isSwitchFlow && displayServiceFee && (
                                    <>
                                        <div className={`${styles.lineItems}`}>
                                            <div className="d-flex align-items-center">Service fee <InfoButton fixedWidth={250} hoverText={`These fees support the essential costs we incur in critical areas like new features, security, reliability/performance, trust and safety and support.`} /> </div>
                                            <span className="float-right">{event.currency.symbol}{serviceFee.toFixed(2)}</span>
                                        </div>
                                    </>
                                )}
                                <div className="text-dark mt4">
                                    {isSwitchFlow ? 'Total' : 'Subtotal'} <span className="float-right">{event.currency.symbol}{(isSwitchFlow ? registrationTotal : subtotal)?.toFixed(2)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="text-medium mt4">
                                <span className="text-dark">Total Fee Due to Club:</span> <span className="text-dark float-right">{props.totalOffPlatform.toFixed(2)}</span>
                            </div>
                        )}
                    </div>
                )}
                <button
                    data-test="divisionsSelectContinue"
                    data-testid="event-reg.add-division.continue-btn"
                    type="button"
                    className="btn btn-primary btn-xl btn-block text-uppercase mb24"
                    disabled={(selectedDivisionIds?.length < 1 || (!isSwitchFlow && props.isTabInvalid('divisions-add')) || requirePowerUpgradeForRegistration || (isRegClosed && !selectedDivisionIds.every(id => adminAddedPaymentRequiredDivisionIds.includes(id))) || isEventCompleted)}
                    onClick={onDivisionSelectionContinueClick}
                >
                    Continue
                </button>
            </div>
            {requirePowerUpgradeForRegistration && (
                <>
                    {user.hasPastDueSubscription ? (
                        <Link
                            data-test="resumePowerButton"
                            data-testid="event-reg.add-division.resume-power-link"
                            className={`btn btn-primary btn-xl btn-block text-uppercase ${styles.upgradeToPowerButton}`}
                            to="/subscriptions"
                            {...getClickAttrs({
                                category: 'Resume Power',
                                context: `Resume Power Click - Event Registration`,
                                location: 'Event Registration - Add Division',
                            })}
                        >
                            Resume Power
                        </Link>
                    ) : (
                        <button
                            data-test="upgradeToPowerButton"
                            data-testid="event-reg.add-division.start-power-btn"
                            className={`btn btn-primary btn-xl btn-block text-uppercase ${styles.upgradeToPowerButton}`}
                            type="button"
                            onClick={props.handleUpgradeToPowerClick}
                            {...getClickAttrs({
                                category: 'Power Upgrade',
                                context: `Start Power Click - Event Registration`,
                                location: 'Event Registration - Add Division',
                            })}
                        >
                            Start Power
                        </button>
                    )}
                    <div className="text-small text-center mt24">Selected divisions contain power only registration, <br /> please {user.hasPastDueSubscription ? 'resume your subscription' : 'upgrade'} to continue.</div>
                </>
            )}
        </>
    );
};

AddDivision.propTypes = {
    user: PropTypes.objectOf(any).isRequired,
    event: PropTypes.objectOf(any).isRequired,
    eventFee: PropTypes.number,
    eventEntryFee: PropTypes.number,
    isEventEntryFeePaid: PropTypes.bool,
    getRestrictedDivisions: PropTypes.func,
    clubVerificationFee: PropTypes.number,
    toggleDivision: PropTypes.func,
    toggleDivisions: PropTypes.func,
    paymentProviderType: PropTypes.number,
    financeAndAdminFee: PropTypes.number,
    registrationTotal: PropTypes.number,
    subtotal: PropTypes.number,
    totalOffPlatform: PropTypes.number,
    handleUpgradeToPowerClick: PropTypes.func,
    toStep: PropTypes.func,
    unRegisteredPlayers: PropTypes.arrayOf(any),
    isTabInvalid: PropTypes.func,
    selectedDivisionIds: PropTypes.arrayOf(any),
    registeredDivisionDates: PropTypes.arrayOf(any),
    canceledDivisionIds: PropTypes.arrayOf(any),
    players: PropTypes.objectOf(any).isRequired,
    declinePartnerInvitation: PropTypes.func,
    updateErrorMsgStatus: PropTypes.func,
    isEditRegisterationPage: PropTypes.bool,
    serviceFee: PropTypes.number,
    displayServiceFee: PropTypes.bool,
    divisionFormatTypeFilter: PropTypes.number,
    switchPlayer2: PropTypes.objectOf(any),
    handleCloseClick: PropTypes.func,
};

AddDivision.defaultProps = {
    eventFee: 0,
    eventEntryFee: 0,
    isEventEntryFeePaid: false,
    getRestrictedDivisions: () => { },
    clubVerificationFee: 0,
    toggleDivision: () => { },
    toggleDivisions: () => { },
    paymentProviderType: 2,
    financeAndAdminFee: 0,
    registrationTotal: 0,
    subtotal: 0,
    totalOffPlatform: 0,
    handleUpgradeToPowerClick: () => { },
    toStep: () => { },
    unRegisteredPlayers: [],
    selectedDivisionIds: [],
    canceledDivisionIds: [],
    registeredDivisionDates: [],
    isTabInvalid: () => { },
    declinePartnerInvitation: () => { },
    updateErrorMsgStatus: () => { },
    isEditRegisterationPage: false,
    serviceFee: 0,
    displayServiceFee: false,
    divisionFormatTypeFilter: null,
    switchPlayer2: null,
    handleCloseClick: () => { },
};

export default AddDivision;
